import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import sortedEvents from "../../../data/sortedEvents";
import type { EventRecord } from "../../../types/dataTypes";
import TypewriterTitle from "../common/TypewriterTitle";

// Extract fun events
const fallbackFunEvents = sortedEvents.filter(
  (e: any) => e.karmatek && e.type === "fun",
);

type Props = {
  events?: EventRecord[];
};

const FunEventsHighlight: React.FC<Props> = ({ events }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  const funEvents = (events?.length ? events : fallbackFunEvents).filter(
    (e: any) => String(e.type || "").toLowerCase() === "fun",
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <section className="mt-4 mb-2 position-relative overflow-hidden rounded-4 karmatek-district karmatek-district--fun">
      <style>{`
          .group:hover .card-overlay { opacity: 1 !important; }
          @media (max-width: 767px) { .card-overlay { opacity: 1 !important; } }
        `}</style>
      <div className="position-relative z-1 text-white py-4 px-3">
        <TypewriterTitle
          text="Fun & Informals"
          className="fw-bold mb-4 display-6 text-center karmatek-section-title"
          speed={35}
        />

        <motion.p
          className="lead text-center text-secondary mb-5"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          Karmatek isn't just about code and combat — join the fun side of the
          fest!
        </motion.p>

        <div className="row justify-content-center">
          {funEvents.map((event: any, idx: number) => (
            <motion.div
              key={event.id || event.slug || idx}
              className="col-12 col-sm-6 col-md-4 col-lg-4 mb-4"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
            >
              <div
                className={`rounded-4 text-center p-3 h-100 position-relative group karmatek-power-card`}
                style={{
                  background: `url(${event.eventImage}) center/cover no-repeat, #222`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  aspectRatio: "1 / 1",
                  borderRadius: "20px",
                  border: "1px solid rgba(255,255,255,0.1)",
                  boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                  cursor: "pointer",
                  overflow: "hidden",
                  position: "relative",
                }}
                onClick={() =>
                  navigate(`/karmatek/event/${event.slug || event.id}`)
                }
                aria-label={`View details for ${event.name}`}
              >
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center card-overlay"
                  style={{
                    background: "rgba(0, 0, 0, 0.6)",
                    opacity: isMobile ? 1 : 0,
                    transition: "opacity 0.3s ease",
                    color: "white",
                    padding: "1rem",
                  }}
                >
                  <h6 className="fw-bold mb-2">{event.name}</h6>
                  {event.subheading && (
                    <div className="text-info text-uppercase fw-semibold mb-1">
                      {event.subheading}
                    </div>
                  )}
                  {event.tagline && (
                    <p className="small mb-2">{event.tagline}</p>
                  )}
                  <div className="d-flex flex-wrap gap-2 justify-content-center mb-3">
                    {event.entryFee && (
                      <span className="badge bg-dark text-light">
                        Entry: {event.entryFee}
                      </span>
                    )}
                    {event.prizePool && (
                      <span className="badge bg-dark text-light">
                        Prize: {event.prizePool}
                      </span>
                    )}
                    {event.mode && (
                      <span className="badge bg-secondary text-light">
                        {event.mode}
                      </span>
                    )}
                  </div>
                  <button
                    className="btn btn-outline-light"
                    aria-label={`View details for ${event.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/karmatek/event/${event.slug || event.id}`);
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FunEventsHighlight;
